import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Shield,
  Upload,
  AlertTriangle,
  CheckCircle,
  Calendar,
  DollarSign,
  FileText,
  Loader2,
  RefreshCw,
  Trash2,
  Edit,
  X,
  Building
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const PropertyInsurance = ({ property, onUpdate }) => {
  const { token, isManager } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    policy_number: "",
    provider: "",
    coverage_type: "comprehensive",
    coverage_amount: "",
    deductible: "",
    start_date: "",
    expiry_date: ""
  });

  const headers = { Authorization: `Bearer ${token}` };
  const insurance = property?.insurance;

  useEffect(() => {
    if (insurance) {
      setFormData({
        policy_number: insurance.policy_number || "",
        provider: insurance.provider || "",
        coverage_type: insurance.coverage_type || "comprehensive",
        coverage_amount: insurance.coverage_amount || "",
        deductible: insurance.deductible || "",
        start_date: insurance.start_date || "",
        expiry_date: insurance.expiry_date || ""
      });
    }
  }, [insurance]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(
        `${API}/properties/${property.id}/insurance/upload`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      toast.success("Insurance document uploaded and analyzed!");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload insurance");
    } finally {
      setUploading(false);
    }
  };

  const handleManualSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/properties/${property.id}/insurance`,
        {
          ...formData,
          coverage_amount: formData.coverage_amount ? parseFloat(formData.coverage_amount) : null,
          deductible: formData.deductible ? parseFloat(formData.deductible) : null
        },
        { headers }
      );
      toast.success("Insurance updated");
      setEditDialogOpen(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to update insurance");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/properties/${property.id}/insurance`, { headers });
      toast.success("Insurance removed");
      setDeleteConfirm(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to remove insurance");
    }
  };

  const getExpiryStatus = () => {
    if (!insurance?.expiry_date) return null;
    
    const today = new Date();
    const expiry = new Date(insurance.expiry_date);
    const daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) {
      return { status: "expired", days: Math.abs(daysUntil), color: "bg-red-100 text-red-700" };
    } else if (daysUntil <= 30) {
      return { status: "expiring", days: daysUntil, color: "bg-amber-100 text-amber-700" };
    } else {
      return { status: "valid", days: daysUntil, color: "bg-green-100 text-green-700" };
    }
  };

  const expiryStatus = getExpiryStatus();

  return (
    <div className="space-y-4" data-testid="property-insurance">
      {!insurance ? (
        /* No Insurance State */
        <Card className="border-dashed border-2 border-amber-300 bg-amber-50">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
            <h3 className="text-lg font-semibold text-amber-800">No Insurance on File</h3>
            <p className="text-amber-600 mt-1 mb-4">
              This property doesn't have insurance documentation
            </p>
            {isManager && (
              <div className="flex justify-center gap-3">
                <label className="cursor-pointer">
                  <Button disabled={uploading} className="bg-green-700 hover:bg-green-800">
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload Insurance Doc
                  </Button>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,image/*"
                    onChange={handleFileUpload}
                    data-testid="insurance-upload"
                  />
                </label>
                <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Enter Manually
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Has Insurance */
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Insurance Coverage
              </CardTitle>
              <div className="flex items-center gap-2">
                {expiryStatus && (
                  <Badge className={expiryStatus.color}>
                    {expiryStatus.status === "expired" 
                      ? `Expired ${expiryStatus.days} days ago`
                      : expiryStatus.status === "expiring"
                      ? `Expires in ${expiryStatus.days} days`
                      : `Valid for ${expiryStatus.days} days`
                    }
                  </Badge>
                )}
                {isManager && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setEditDialogOpen(true)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleteConfirm(true)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Provider</p>
                <p className="font-semibold">{insurance.provider || "N/A"}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Policy Number</p>
                <p className="font-semibold">{insurance.policy_number || "N/A"}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Coverage Type</p>
                <p className="font-semibold capitalize">{insurance.coverage_type || "N/A"}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Expiry Date</p>
                <p className="font-semibold">{insurance.expiry_date || "N/A"}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Coverage Amount</span>
                </div>
                <p className="text-2xl font-bold text-green-700">
                  {insurance.coverage_amount 
                    ? `$${insurance.coverage_amount.toLocaleString()}`
                    : "N/A"
                  }
                </p>
              </div>
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Deductible</span>
                </div>
                <p className="text-2xl font-bold text-slate-700">
                  {insurance.deductible 
                    ? `$${insurance.deductible.toLocaleString()}`
                    : "N/A"
                  }
                </p>
              </div>
            </div>

            {/* AI Warnings */}
            {insurance.ai_notes && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">AI Analysis Note</p>
                    <p className="text-amber-700 text-sm mt-1">{insurance.ai_notes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Document Info */}
            {insurance.document_name && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">{insurance.document_name}</span>
                </div>
                {insurance.ai_parsed && (
                  <Badge variant="outline" className="text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    AI Verified
                  </Badge>
                )}
              </div>
            )}

            {/* Upload New */}
            {isManager && (
              <label className="cursor-pointer block">
                <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-slate-50 transition-colors">
                  {uploading ? (
                    <Loader2 className="w-6 h-6 mx-auto animate-spin text-green-600" />
                  ) : (
                    <>
                      <RefreshCw className="w-6 h-6 mx-auto text-slate-400" />
                      <p className="text-sm text-slate-500 mt-2">Upload new insurance document</p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,image/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
              </label>
            )}
          </CardContent>
        </Card>
      )}

      {/* Manual Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Insurance Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Provider</Label>
                <Input
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  placeholder="Insurance Company"
                />
              </div>
              <div className="space-y-2">
                <Label>Policy Number</Label>
                <Input
                  value={formData.policy_number}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                  placeholder="POL-123456"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Coverage Type</Label>
              <Select
                value={formData.coverage_type}
                onValueChange={(v) => setFormData({ ...formData, coverage_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="liability">Liability</SelectItem>
                  <SelectItem value="property">Property</SelectItem>
                  <SelectItem value="comprehensive">Comprehensive</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Coverage Amount ($)</Label>
                <Input
                  type="number"
                  value={formData.coverage_amount}
                  onChange={(e) => setFormData({ ...formData, coverage_amount: e.target.value })}
                  placeholder="500000"
                />
              </div>
              <div className="space-y-2">
                <Label>Deductible ($)</Label>
                <Input
                  type="number"
                  value={formData.deductible}
                  onChange={(e) => setFormData({ ...formData, deductible: e.target.value })}
                  placeholder="1000"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleManualSave} disabled={saving} className="bg-green-700 hover:bg-green-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Insurance?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the insurance information from this property. The document will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PropertyInsurance;
